import React, { useEffect, useState } from 'react';
import { numberWithCommas } from '@components/common';
import { Button } from '@components/common/Button';
import DateRangePicker, { DateRangePreset } from '@components/common/DateRangePicker';
import Table, { TableHeaderContainerV2 } from '@components/common/Table';
import MileageAddModal from '@components/modal/mileageAddModal';
import PaginationComponent from '@components/pagination';
import { mileageColumn2 } from '@data/columns';
import { getMileageUser } from '@lib/api/v2/mileage';
import dayjs from '@lib/dayjs';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import Format from '@util/formatter';
import styled from 'styled-components';

const TableHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

interface MileageTabProps {
  merchantId: number;
  userId: number;
  seat: any;
  setIsLoading: (loading: boolean) => void;
}

const MileageTab = ({ merchantId, userId, seat, setIsLoading }: MileageTabProps) => {
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();

  const [startDate, setStartDate] = useState(dayjs().subtract(3, 'month').add(1, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const [totalLength, setTotalLength] = useState(0);
  const [dataList, setDataList] = useState<any[]>([]);
  const [mileageBalance, setMileageBalance] = useState(0);
  const [isMileageAdd, setIsMileageAdd] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const dateRange = Format.formatDateRange(startDate, endDate);
      const { data } = await createRequest(
        getMileageUser,
        seat.id,
        dateRange.start,
        dateRange.end,
        currentPage,
      );
      setDataList(
        data.userMileageList.map((item) => ({
          ...item,
          usingTime: dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss'),
          price: item.amount === 0 ? 0 : !item.amount ? '-' : numberWithCommas(item.amount),
        })),
      );
      setMileageBalance(data.userTotalMileage);
      setTotalLength(data.userMileageCount);
    } catch (error) {
      catchError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (seat?.id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seat?.id, currentPage, startDate, endDate]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'startDate') setStartDate(value);
    else if (name === 'endDate') setEndDate(value);
    setCurrentPage(1);
  };

  const toggleMileageModal = () => {
    setIsMileageAdd(!isMileageAdd);
  };

  return (
    <>
      {isMileageAdd && (
        <MileageAddModal
          isOpen={isMileageAdd}
          onClose={toggleMileageModal}
          user={seat}
          userId={userId}
          reset={fetchData}
        />
      )}
      <TableHeader>
        <div>잔여 마일리지 : {Format.formatNumber(mileageBalance)}P</div>
        <Button buttonType="action" onClick={toggleMileageModal}>
          마일리지 수동 적립/차감
        </Button>
      </TableHeader>
      <TableHeaderContainerV2>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateChange}
          defaultPreset={DateRangePreset.QUARTER_YEAR}
          maxPeriod={DateRangePreset.YEAR}
        />
      </TableHeaderContainerV2>
      <div className="datatable-container">
        <Table tableHeight={500} data={dataList} columns={mileageColumn2} striped={true} />
      </div>
      {totalLength > 0 && (
        <PaginationComponent
          setCurrentPage={setCurrentPage}
          totalLength={totalLength}
          postsPerPage={postsPerPage}
          currentPage={currentPage}
        />
      )}
    </>
  );
};

export default MileageTab;
